import React from 'react';
import type { SharedMemory } from '../../types/memory';
import './CompatibilityBadge.css';

interface CompatibilityBadgeProps {
  /** 0..1 compatibility score from the backend (WU-B3 / §6.1). */
  score?: number;
  /** Mutually-confirmed shared overlaps; used only for the tooltip / aria summary. */
  sharedMemories?: SharedMemory[];
  className?: string;
}

/**
 * Subtle compatibility ring (mirrors the verified-badge), render-only.
 * Returns null when no score is known — the backend omits it when there is
 * no shared, confidently-known dimension, and the UI never fabricates one.
 * The arc is fed by a numeric `--compat-pct` custom prop (not a color).
 *
 * V2 Amethyst WU-02 (R4): the badge's text is explainable-facts-only — the
 * shared phrase now renders as a visible caption (not just a hover title,
 * which mobile/touch never sees), falling back to the bare percentage when
 * no shared fact is known. WU-05 will wire real matching reasons in later.
 */
export const CompatibilityBadge: React.FC<CompatibilityBadgeProps> = ({
  score,
  sharedMemories,
  className,
}) => {
  if (score == null) {
    return null;
  }

  const pct = Math.round(score * 100);
  // Only the backend-provided neutral canonical phrase (never raw memory text).
  const phrase = sharedMemories?.[0]?.phrase;
  const title = phrase ? `${pct}% compatible — you both ${phrase}` : `${pct}% compatible`;

  return (
    <div className={`compatibility-badge-row${className ? ` ${className}` : ''}`}>
      <div
        className="compatibility-badge"
        style={{ '--compat-pct': pct } as React.CSSProperties}
        role="img"
        aria-label={`${pct}% compatible`}
        title={title}
      >
        <span className="compat-pct">{pct}%</span>
      </div>
      {phrase && <span className="compatibility-badge-caption">you both {phrase}</span>}
    </div>
  );
};

export default CompatibilityBadge;
